import {
	getCustomerSelectValue,
	hasCustomerIdentity,
	JAB_ORG_ID,
	normalizeCustomer,
	normalizeCustomerName,
	resolveCustomerIdentity
} from "./customerIdentityUtils";

export const JOB_CUSTOMER_IDENTITY_SNAPSHOT_VERSION = 1;
export const JAB_INTERNAL_COMPANY_NAME = "Converge Custom";

function normalizeString(value) {
	return value === undefined || value === null ? "" : String(value).trim();
}

/**
 * Return the only customer payload that may be persisted by a reassignment.
 * Keeping this shape strict prevents a member profile or catalog row from
 * leaking unrelated metadata into an instance.
 */
export function normalizeCustomerIdentitySnapshot(identity = {}) {
	const normalizedCustomer = normalizeCustomer(identity) || {};
	return {
		version: JOB_CUSTOMER_IDENTITY_SNAPSHOT_VERSION,
		company: normalizeString(normalizedCustomer.customerName),
		customerGuid: normalizeString(normalizedCustomer.customerGuid),
		customerCode: normalizeString(normalizedCustomer.customerCode)
	};
}

function parseMemberInfo(member = {}) {
	const rawMemberInfo = member && Object.prototype.hasOwnProperty.call(member, "memberInfo")
		? member.memberInfo
		: member;

	if (typeof rawMemberInfo === "string") {
		if (!rawMemberInfo.trim()) {
			return {};
		}
		try {
			const parsed = JSON.parse(rawMemberInfo);
			return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
		} catch (err) {
			return {};
		}
	}

	return rawMemberInfo && typeof rawMemberInfo === "object" && !Array.isArray(rawMemberInfo)
		? rawMemberInfo
		: {};
}

export function getMemberCustomerIdentity(member = {}) {
	return normalizeCustomerIdentitySnapshot(parseMemberInfo(member));
}

function isInternalStaffMember(member = {}) {
	const hasRole = value => value === true || value === 1 || value === "1";
	return Boolean(member && (hasRole(member.administrator) || hasRole(member.author)));
}

function isInternalJabCustomer(identity = {}) {
	return normalizeCustomerName(identity.company || identity.customerName) === normalizeCustomerName(JAB_INTERNAL_COMPANY_NAME);
}

export function isCompleteCustomerIdentitySnapshot(snapshot = {}) {
	if (!snapshot || typeof snapshot !== "object" || Array.isArray(snapshot)) {
		return false;
	}
	const expectedKeys = ["company", "customerCode", "customerGuid", "version"];
	const actualKeys = Object.keys(snapshot).sort();
	return actualKeys.length === expectedKeys.length
		&& actualKeys.every((key, index) => key === expectedKeys[index])
		&& snapshot.version === JOB_CUSTOMER_IDENTITY_SNAPSHOT_VERSION
		&& Boolean(
			normalizeString(snapshot.company)
			&& normalizeString(snapshot.customerGuid)
			&& normalizeString(snapshot.customerCode)
		);
}

export function buildJobReassignmentEdge({
	timestamp,
	identityId,
	srcInstanceUrl,
	dstInstanceUrl,
	customerIdentitySnapshot = null
} = {}) {
	const edge = {
		timestamp: normalizeString(timestamp),
		identityId: normalizeString(identityId),
		srcInstanceUrl: normalizeString(srcInstanceUrl),
		dstInstanceUrl: normalizeString(dstInstanceUrl)
	};
	if (!edge.timestamp || !edge.identityId || !edge.srcInstanceUrl || !edge.dstInstanceUrl) {
		throw new Error("A reassignment edge requires a timestamp, identity, source, and destination.");
	}

	if (customerIdentitySnapshot) {
		const normalizedSnapshot = normalizeCustomerIdentitySnapshot(customerIdentitySnapshot);
		if (!isCompleteCustomerIdentitySnapshot(normalizedSnapshot)) {
			throw new Error("A customer transfer requires a complete customer identity snapshot.");
		}
		edge.customerIdentitySnapshot = normalizedSnapshot;
	}

	return edge;
}

function resolveCanonicalCustomer(customerIdentityMaps, identity) {
	try {
		return resolveCustomerIdentity(customerIdentityMaps, identity);
	} catch (err) {
		return {
			status: "unresolved",
			customer: null
		};
	}
}

function sameCustomer(left, right) {
	const leftKey = getCustomerSelectValue(left);
	const rightKey = getCustomerSelectValue(right);
	return Boolean(leftKey && rightKey && leftKey === rightKey);
}

function buildPlan({
	customerAction = "preserve",
	requiresConfirmation = false,
	reason,
	currentCustomerIdentitySnapshot,
	targetCustomerIdentitySnapshot,
	customerTransferBlocked = false
}) {
	const shouldTransferCustomer = customerAction === "transfer";
	return {
		customerAction,
		shouldTransferCustomer,
		requiresConfirmation,
		reason,
		customerTransferBlocked,
		currentCustomerIdentitySnapshot,
		targetCustomerIdentitySnapshot,
		customerIdentitySnapshot: shouldTransferCustomer ? targetCustomerIdentitySnapshot : null
	};
}

/**
 * Decide whether a single JAB Reassign operation also changes the customer.
 *
 * Reassigning to internal staff never changes the customer. Moving an
 * internal/unassigned job to a customer member adopts that member's canonical
 * customer automatically. Moving between two established customers requires
 * confirmation. Any unresolved metadata fails closed and preserves the
 * existing customer.
 */
export function buildJobReassignmentPlan({
	orgId,
	currentCustomerIdentity = {},
	targetMember = {},
	customerIdentityMaps = null
} = {}) {
	const currentRawSnapshot = normalizeCustomerIdentitySnapshot(currentCustomerIdentity);
	const targetRawSnapshot = getMemberCustomerIdentity(targetMember);
	const basePlan = {
		currentCustomerIdentitySnapshot: currentRawSnapshot,
		targetCustomerIdentitySnapshot: targetRawSnapshot
	};

	if (orgId !== JAB_ORG_ID) {
		return buildPlan({
			...basePlan,
			reason: "organization-not-supported"
		});
	}

	if (isInternalStaffMember(targetMember) || !hasCustomerIdentity(targetRawSnapshot) || isInternalJabCustomer(targetRawSnapshot)) {
		return buildPlan({
			...basePlan,
			reason: "target-internal-staff"
		});
	}

	const targetResolution = resolveCanonicalCustomer(customerIdentityMaps, targetRawSnapshot);
	if (targetResolution.status !== "resolved" || !targetResolution.customer) {
		return buildPlan({
			...basePlan,
			reason: "target-customer-unresolved",
			customerTransferBlocked: true
		});
	}

	const targetCustomerIdentitySnapshot = normalizeCustomerIdentitySnapshot(targetResolution.customer);
	const resolvedTargetPlan = {
		...basePlan,
		targetCustomerIdentitySnapshot
	};

	if (isInternalJabCustomer(targetCustomerIdentitySnapshot)) {
		return buildPlan({
			...resolvedTargetPlan,
			reason: "target-internal-staff"
		});
	}

	if (!isCompleteCustomerIdentitySnapshot(targetCustomerIdentitySnapshot)) {
		return buildPlan({
			...resolvedTargetPlan,
			reason: "target-customer-incomplete",
			customerTransferBlocked: true
		});
	}

	if (!hasCustomerIdentity(currentRawSnapshot) || isInternalJabCustomer(currentRawSnapshot)) {
		return buildPlan({
			...resolvedTargetPlan,
			customerAction: "transfer",
			reason: !hasCustomerIdentity(currentRawSnapshot)
				? "current-customer-unassigned"
				: "current-customer-internal"
		});
	}

	const currentResolution = resolveCanonicalCustomer(customerIdentityMaps, currentRawSnapshot);
	if (currentResolution.status !== "resolved" || !currentResolution.customer) {
		return buildPlan({
			...resolvedTargetPlan,
			reason: "current-customer-unresolved",
			customerTransferBlocked: true
		});
	}

	const currentCustomerIdentitySnapshot = normalizeCustomerIdentitySnapshot(currentResolution.customer);
	const resolvedPlan = {
		...resolvedTargetPlan,
		currentCustomerIdentitySnapshot
	};

	if (isInternalJabCustomer(currentCustomerIdentitySnapshot)) {
		return buildPlan({
			...resolvedPlan,
			customerAction: "transfer",
			reason: "current-customer-internal"
		});
	}

	if (sameCustomer(currentResolution.customer, targetResolution.customer)) {
		return buildPlan({
			...resolvedPlan,
			reason: "same-customer"
		});
	}

	return buildPlan({
		...resolvedPlan,
		customerAction: "transfer",
		requiresConfirmation: true,
		reason: "customer-change"
	});
}

function getOwnerFromInstanceUrl(instanceUrl) {
	if (typeof instanceUrl !== "string" || !instanceUrl.trim()) {
		return "";
	}

	let key = instanceUrl.trim();
	if (key.indexOf("://") !== -1) {
		try {
			key = new URL(key).pathname;
		} catch (err) {
			return "";
		}
	}
	key = key.split(/[?#]/)[0];
	const parts = key.split("/").filter(Boolean);
	return parts.length >= 3 ? normalizeString(parts[0]) : "";
}

/**
 * The current object's S3 key prefix is authoritative. Assignee history is a
 * fallback only when a caller does not have the key.
 */
export function getCurrentInstanceOwnerIdentityId(instanceUrl, instance = {}) {
	const ownerFromUrl = getOwnerFromInstanceUrl(instanceUrl);
	if (ownerFromUrl) {
		return ownerFromUrl;
	}

	const assignees = instance && Array.isArray(instance.assignees) ? instance.assignees : [];
	for (let index = assignees.length - 1; index >= 0; index--) {
		const identityId = normalizeString(assignees[index] && assignees[index].identityId);
		if (identityId) {
			return identityId;
		}
	}

	const instanceIdentityId = normalizeString(instance && instance.identityId);
	if (instanceIdentityId) {
		return instanceIdentityId;
	}

	const history = instance && Array.isArray(instance.history) ? instance.history : [];
	return normalizeString(history[0] && history[0].identityId);
}
